import React, { useRef, lazy, Suspense, useCallback } from 'react';
import { recordProgress, progressFor, COMPLETE_AT } from '../../utils/educationLibrary';
import { RotateCcw, RotateCw } from 'lucide-react';
import { isMuxReady } from '../../utils/educationContent';

const ACCENT = '#2563eb';

// Mux Player (with its HLS engine) is ~300 kB gzipped; fetch it only when a Mux video is opened
const MuxPlayer = lazy(() => import('@mux/mux-player-react'));

// "16:9" from Mux -> CSS aspect-ratio, so the player has its final height before the stream loads
const cssAspectRatio = (ratio) => {
  const [w, h] = String(ratio || '').split(':').map(Number);
  return w && h ? `${w} / ${h}` : '16 / 9';
};

/**
 * Plays an education video: Mux Player (adaptive bitrate, built-in 10s seek
 * buttons) when the item has been encoded by Mux, otherwise the original
 * progressive file from Storage with the app's own seek overlays.
 */
const EducationVideoPlayer = ({ content, className = '', frame = 'rounded' }) => {
  const videoRef = useRef(null);
  const lastSaved = useRef(0);

  // Where to pick up: the saved position unless the video was all but finished
  const saved = progressFor(content.id);
  const resumeAt = saved && !saved.completed && saved.duration && saved.position > 5 && saved.position / saved.duration < COMPLETE_AT ? saved.position : 0;

  // Save every few seconds while playing, and always on pause or end
  const report = useCallback((el, force = false) => {
    if (!el || !(el.duration > 0)) return;
    const now = Date.now();
    if (!force && now - lastSaved.current < 5000) return;
    lastSaved.current = now;
    recordProgress(content, el.currentTime, el.duration);
  }, [content]);
  const onTimeUpdate = (e) => report(e.currentTarget || e.target);
  const onPause = (e) => report(e.currentTarget || e.target, true);
  const onEnded = (e) => { const el = e.currentTarget || e.target; if (el) recordProgress(content, el.duration, el.duration); };
  // 'rounded' = standalone card look; 'bare' = edge to edge inside a container that shapes it
  const shape = frame === 'bare' ? '' : 'rounded-lg overflow-hidden shadow-inner';

  if (isMuxReady(content)) {
    return (
      <div
        className={`${shape} bg-black ${className}`}
        style={{ aspectRatio: cssAspectRatio(content.aspectRatio) }}
      >
        <Suspense fallback={<div className="w-full h-full bg-black animate-pulse" />}>
        <MuxPlayer
          playbackId={content.muxPlaybackId}
          streamType="on-demand"
          playsInline
          preload="metadata"
          accentColor={ACCENT}
          forwardSeekOffset={10}
          backwardSeekOffset={10}
          thumbnailTime={content.thumbnailTime ?? 1}
          poster={content.thumbnailURL || undefined}
          startTime={resumeAt || undefined}
          onTimeUpdate={onTimeUpdate}
          onPause={onPause}
          onEnded={onEnded}
          metadata={{
            video_id: content.id,
            video_title: content.title,
            video_series: content.series || undefined,
          }}
          style={{ width: '100%', height: '100%' }}
        />
        </Suspense>
      </div>
    );
  }

  if (!content?.downloadURL) {
    return (
      <div className={`${frame === 'bare' ? '' : 'rounded-lg'} bg-gray-100 dark:bg-gray-800 p-6 text-center text-sm text-gray-600 dark:text-gray-300 ${className}`}>
        This video is still being processed. Check back in a few minutes.
      </div>
    );
  }

  const skip = (seconds) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.min(video.duration || Infinity, Math.max(0, video.currentTime + seconds));
  };

  const overlayClass =
    'absolute top-1/2 -translate-y-1/2 bg-black/70 hover:bg-black/90 text-white rounded-full p-3 opacity-0 group-hover:opacity-100 transition-all duration-200 backdrop-blur-md shadow-lg hover:shadow-xl hover:scale-110 active:scale-95';

  return (
    <div className={`relative group ${shape} ${className}`}>
      <video
        ref={videoRef}
        controls
        playsInline
        className="w-full"
        src={content.downloadURL}
        onLoadedMetadata={(e) => { if (resumeAt) e.currentTarget.currentTime = resumeAt; }}
        onTimeUpdate={onTimeUpdate}
        onPause={onPause}
        onEnded={onEnded}
      >
        Your browser does not support the video tag.
      </video>
      <button onClick={() => skip(-10)} className={`${overlayClass} left-4`} aria-label="Skip backward 10 seconds">
        <div className="flex items-center gap-1">
          <RotateCcw size={20} />
          <span className="text-xs font-bold">10</span>
        </div>
      </button>
      <button onClick={() => skip(10)} className={`${overlayClass} right-4`} aria-label="Skip forward 10 seconds">
        <div className="flex items-center gap-1">
          <span className="text-xs font-bold">10</span>
          <RotateCw size={20} />
        </div>
      </button>
    </div>
  );
};

export default EducationVideoPlayer;
